import { Trans } from "@lingui/react/macro";
import React, { ReactElement } from "react";
import {
  BaseCard,
  BaseCardTitle,
  BaseCardBody,
  BaseCardFooter,
  Button,
  ImageAspectRatio,
  BaseCardSanityImage,
  BaseCardSanityImageProps,
  BaseCardBorderColor,
  BackgroundColor
} from "@silversea-ssc/ui-components-react";

import { isPresent } from "src/utils/checks";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import { formatDate } from "src/utils/date";
import ExpiresOnChip from "components/ExpiresOnChip/ExpiresOnChip";
import * as styles from "./LinkCard.module.scss";
import { WrapWithLink, actionFromCardAction } from "components/WrapWithLink";
import { match, P } from "ts-pattern";
import clsx from "clsx";
import { CardAction } from "./cardAction";
import CardTags from "components/CardTags/CardTags";

export type LinkCardProps = {
  title: string | ReactElement | undefined;
  description?: string | ReactElement | undefined;
  imageAltText?: string;
  image?: BaseCardSanityImageProps["image"];
  imageRatio?: ImageAspectRatio;
  backgroundColor?: BackgroundColor;
  borderColor?: BaseCardBorderColor;
  trackingName?: string;
  style?: React.CSSProperties;
  expirationDate?: Date;
  action: CardAction | undefined;
  tags?: ReactElement;
};

const LinkCard: React.FC<React.PropsWithChildren<LinkCardProps>> = ({
  title,
  description,
  image,
  imageAltText,
  backgroundColor = "white",
  trackingName,
  imageRatio = "card",
  borderColor,
  style,
  expirationDate,
  action,
  tags
}) => {
  const altText =
    imageAltText || (typeof title == "string" ? title : undefined);

  const language = useLanguage();
  const formattedDate = expirationDate
    ? formatDate(expirationDate, language.ogLang, {
        day: "numeric",
        month: "long",
        year: "numeric"
      })
    : undefined;

  const footerButton = match(action)
    .with(P.nullish, () => undefined)
    .with({ button: P.nonNullable }, ({ button }) => button)
    .with({ href: P.string }, ({ href, openInNewTab, linkLabel }) => (
      <Button
        dataContentPiece="more_button"
        variant="inline-text"
        href={href}
        target={openInNewTab ? "_blank" : undefined}
        iconName="arrow-right"
        rel="nofollow"
      >
        {linkLabel || <Trans id="cta-discover-more">Discover more</Trans>}
      </Button>
    ))
    .with({ onClick: P.nonNullable }, ({ onClick }) => (
      <Button
        renderAs="button"
        dataContentPiece="more_button"
        variant="inline-text"
        iconName="arrow-right"
        onClick={onClick}
      >
        <Trans id="cta-discover-more">Discover more</Trans>
      </Button>
    ))
    .otherwise(() => undefined);

  return (
    <BaseCard
      background={backgroundColor}
      trackingName={trackingName}
      style={style}
    >
      <WrapWithLink
        {...actionFromCardAction(action)}
        rel="nofollow"
        data-content-piece="more_image"
        aria-hidden={true}
        tabIndex={-1}
      >
        {formattedDate && (
          <div className={styles.expiresOnChip}>
            <ExpiresOnChip background="white">
              <Trans>Expires on {formattedDate}</Trans>
            </ExpiresOnChip>
          </div>
        )}
        {tags && <CardTags position="absolute">{tags}</CardTags>}
        {image && (
          <BaseCardSanityImage
            image={image}
            ratio={imageRatio}
            alt={altText}
            borderColor={borderColor}
          ></BaseCardSanityImage>
        )}
      </WrapWithLink>
      {title && (
        <BaseCardTitle
          className={clsx({
            [styles.titleNoDescription]: !isPresent(description)
          })}
        >
          <WrapWithLink
            {...actionFromCardAction(action)}
            data-content-piece="more_title"
          >
            {title}
          </WrapWithLink>
        </BaseCardTitle>
      )}
      {description && <BaseCardBody>{description}</BaseCardBody>}

      {footerButton && <BaseCardFooter>{footerButton}</BaseCardFooter>}
    </BaseCard>
  );
};

export default React.memo(LinkCard);
